import { useEffect, useRef, useState } from "react";

import { faCheck, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { getEnumError, isValidEnumOptionInput } from "src/schema/enumUtils";

type SchemaEnumItemInputProps = {
  disabled?: boolean;
  value: string;
  dataLoc: string;
  onCommit: (value: string) => void;
  onBlur?: () => void;
  onCancel: () => void;
};

export const SchemaEnumItemInput: React.FC<SchemaEnumItemInputProps> = ({
  disabled = false,
  value,
  dataLoc,
  onCommit,
  onBlur,
  onCancel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(value);

  const evaledError = (() => {
    if (!localValue) {
      return getEnumError("required");
    }
    if (!isValidEnumOptionInput(localValue)) {
      return getEnumError("validValue");
    }
    return null;
  })();

  const handleCommit = () => {
    onCommit(localValue);
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.select();
    }
  }, []);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <Tooltip
      body={evaledError?.description ?? ""}
      disabled={!evaledError}
      title={evaledError?.title ?? ""}
      asChild
    >
      <div
        className={twJoin(
          "flex items-center gap-x-px",
          "py-1.5 pr-2 pl-3",
          "rounded-lg border",
          !evaledError && "border-gray-200",
          !evaledError &&
            "focus-within:border-indigo-600 focus-within:ring-2 focus-within:ring-indigo-600/25",
          evaledError && "border-red-600 ring-2 ring-red-400/25",
        )}
      >
        <input
          ref={inputRef}
          className="w-15 font-inter-normal-12px text-gray-800 focus:outline-none"
          data-loc={dataLoc}
          disabled={disabled}
          value={localValue}
          autoFocus
          onBlur={onBlur}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleCommit();
            } else if (e.key === "Escape") {
              setLocalValue(value);
              onCancel();
            }
          }}
        />
        <IconButton
          dataLoc={dataLoc ? `${dataLoc}-commit` : undefined}
          icon={faCheck}
          size="xs"
          onClick={handleCommit}
        />
        <IconButton
          dataLoc={dataLoc ? `${dataLoc}-cancel` : undefined}
          icon={faTimes}
          size="xs"
          onClick={onCancel}
        />
      </div>
    </Tooltip>
  );
};
